import { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { SUPPORT_PHONE, SUPPORT_PHONE_TEL } from '../constants/site';
import { submitLead } from '../utils/submitLead';

export default function Hero() {
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setPending(true);

    const form = event.currentTarget;
    const data = new FormData(form);

    try {
      await submitLead({
        name: String(data.get('name') || '').trim(),
        phone: String(data.get('phone') || '').trim(),
        email: String(data.get('email') || '').trim(),
        message: String(data.get('issue') || '').trim(),
        source: 'hero-form',
        _honey: String(data.get('_honey') || ''),
      });
      setSent(true);
      form.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="hero">
      <div className="container">
        <div className="hero__grid">
          <div>
            <p className="hero__badge">
              <Icon name="shield" size={18} fill />
              McAfee Authorised Reseller
            </p>

            <h1 className="hero__title">
              Total Protection.
              <span className="hero__title-accent">Best Price Guaranteed.</span>
            </h1>

            <p className="hero__text">
              Antivirus, VPN, identity monitoring, and family safety in one subscription — set up
              for you by a real specialist, not a help article. Protect every device in your home
              from a single account.
            </p>

            <div className="hero__cta">
              <a className="call-btn" href={`tel:${SUPPORT_PHONE_TEL}`}>
                <span className="call-btn__icon">
                  <Icon name="phone" size={20} strokeWidth={2} />
                </span>
                <span className="call-btn__text">
                  <span className="call-btn__num">{SUPPORT_PHONE}</span>
                  <span className="call-btn__label">Call for today&apos;s price</span>
                </span>
              </a>
              <Link to="/#plans" className="btn btn--ghost btn--lg">
                View Plans
              </Link>
            </div>

            <div className="hero__note">
              <span>
                <Icon name="check" size={16} strokeWidth={2.4} /> 30-day money-back guarantee
              </span>
              <span>
                <Icon name="check" size={16} strokeWidth={2.4} /> Instant digital delivery
              </span>
              <span>
                <Icon name="check" size={16} strokeWidth={2.4} /> Free installation help
              </span>
            </div>
          </div>

          <div className="hero__form-wrap">
            {sent ? (
              <div className="hero-form hero-form--done" role="status">
                <span className="hero-form__done-icon">
                  <Icon name="check" size={28} strokeWidth={2.4} />
                </span>
                <h2>Message sent</h2>
                <p>
                  Thanks — a specialist will follow up shortly. Prefer to talk now? Call{' '}
                  <a href={`tel:${SUPPORT_PHONE_TEL}`}>{SUPPORT_PHONE}</a>.
                </p>
                <button
                  type="button"
                  className="btn btn--outline"
                  onClick={() => setSent(false)}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form className="hero-form" onSubmit={handleSubmit} noValidate>
                <div className="hero-form__head">
                  <h2>Send us a Query</h2>
                  <p>Tell us what you need — we reply fast.</p>
                </div>

                <label>
                  Name
                  <input
                    type="text"
                    name="name"
                    autoComplete="name"
                    required
                    placeholder="Your full name"
                    disabled={pending}
                  />
                </label>

                <label>
                  Phone number
                  <input
                    type="tel"
                    name="phone"
                    autoComplete="tel"
                    required
                    placeholder="Best number to reach you"
                    disabled={pending}
                  />
                </label>

                <label>
                  Email
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    placeholder="you@example.com"
                    disabled={pending}
                  />
                </label>

                <label>
                  Issue
                  <textarea
                    name="issue"
                    rows={3}
                    required
                    placeholder="Describe your issue or what you need help with"
                    disabled={pending}
                  />
                </label>

                {/* Honeypot — leave empty */}
                <input
                  type="text"
                  name="_honey"
                  tabIndex={-1}
                  autoComplete="off"
                  className="hero-form__honey"
                  aria-hidden="true"
                />

                {error && (
                  <p className="hero-form__error" role="alert">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="btn btn--red btn--block btn--lg"
                  disabled={pending}
                >
                  {pending ? 'Sending…' : 'Submit request'}
                </button>

                <p className="hero-form__fine">
                  By submitting you agree to our{' '}
                  <Link to="/privacy-policy">Privacy Policy</Link>.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
